/*
 * Full fix — native submissions only.
 *
 * This store runs Shopify's storefront-forms hCaptcha integration
 * (ce_storefront_forms_captcha_hcaptcha). It binds to every create_customer
 * form: it REPLACES form.submit with its own captcha routine and adds a
 * submit listener that always calls preventDefault() and re-submits the form
 * natively once the captcha has produced a token — regardless of what other
 * listeners do.
 *
 * The old fetch()-based interception in this file raced that integration:
 * every click produced an unverified AJAX POST (which Shopify declines to
 * create a session for, and which silently consumes the one-time "email
 * already associated" flash message) alongside the captcha's own native
 * submission that navigates the browser to the login page.
 *
 * So this element no longer touches the submit flow at all. Outcome
 * messaging lives on the destination pages: templates/customers/login.liquid
 * shows an "email already registered" popup when Shopify's flash arrives,
 * and an "application received" popup when the return_url carries the
 * from_registration marker set by the register sections' return_to.
 *
 * The element still injects the always-visible existing-account notice on
 * register sections that do not render one in Liquid, and this file still
 * defines CustomerAddresses (country/province selects) used by the sections.
 */
if (!customElements.get('register-form')) {
  class RegisterForm extends HTMLElement {
    constructor() {
      super();
      this.init();
      this.ensureNoticeStyles();
      const form =
        this.vetForm || this.ownerForm || this.querySelector('form.register-form');
      if (form) this.ensureNotice(form);
    }

    init () {
      this.vetForm = document.querySelector("[data-vet-registration] form");
      this.ownerForm = document.querySelector("[data-affiliate-registration] form");
    }

    // Resolves a URL and returns a same-site relative path, or null when the
    // URL points at another origin — links we inject never leave the site.
    safePath (url) {
      if (!url) return null;
      try {
        const resolved = new URL(url, window.location.origin);
        if (resolved.origin !== window.location.origin) return null;
        return resolved.pathname + resolved.search + resolved.hash;
      } catch (error) {
        return null;
      }
    }

    getConfig () {
      const root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return {
        contactUrl: this.safePath(this.dataset.contactUrl) || root + 'pages/contact'
      };
    }

    ensureNoticeStyles () {
      if (document.getElementById('register-status-styles')) return;
      const style = document.createElement('style');
      style.id = 'register-status-styles';
      style.textContent = [
        '.register-info-notice{padding:14px 20px;margin:0 0 24px;border-radius:8px;background:#fff8e1;border:1px solid #f0c36d;color:#5c4400;font-size:1.4rem;line-height:1.5;}',
        '.register-info-notice[hidden]{display:none!important;}',
        '.register-info-notice p{margin:0;}',
        '.register-info-notice a{color:inherit;text-decoration:underline;font-weight:600;}'
      ].join('\n');
      document.head.appendChild(style);
    }

    // The "Register Form Builder" section renders its own notice in Liquid.
    // Other register sections reuse this element, so inject a default notice
    // when the markup does not provide one — the warning must always show.
    ensureNotice (form) {
      if (this.querySelector('[data-register-existing-notice]')) return;
      const config = this.getConfig();
      const notice = document.createElement('div');
      notice.className = 'register-info-notice';
      notice.setAttribute('data-register-existing-notice', '');
      const p = document.createElement('p');
      const strong = document.createElement('strong');
      strong.textContent = 'Already have an account with us? ';
      const link = document.createElement('a');
      link.href = config.contactUrl;
      link.textContent = 'contact us';
      p.appendChild(strong);
      p.appendChild(document.createTextNode('If your email address is already registered (for example as a pet-owner account) you may not be able to complete this form. Please '));
      p.appendChild(link);
      p.appendChild(document.createTextNode(' and our team will set up your partner account manually.'));
      notice.appendChild(p);
      form.parentNode.insertBefore(notice, form);
    }
  }
  customElements.define('register-form', RegisterForm);
}

const attributes = {
  expanded: 'aria-expanded',
  confirmMessage: 'data-confirm-message'
};

class CustomerAddresses {
  constructor() {
    this._setupCountries();
  }

  _setupCountries() {
    if (Shopify && Shopify.CountryProvinceSelector) {
      // eslint-disable-next-line no-new
      new Shopify.CountryProvinceSelector('AddressCountryNew', 'AddressProvinceNew', {
        hideElement: 'AddressProvinceContainerNew'
      });
    }
  }

  _toggleExpanded(target) {
    target.setAttribute(
      attributes.expanded,
      (target.getAttribute(attributes.expanded) === 'false').toString()
    );
  }
}
